import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { doc, FieldPath, getDoc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../../firebase/config";
import PageShell from "../layout/PageShell";
import {
  POS_INFORMATION_GROUPS,
  formatPosInformation,
  notePair,
  posInformationDetailKey,
  posInformationKey,
  usePosInformationNotes,
} from "./posInformation";

const emptyPair = { en: "", fr: "" };

const PosInformationPage: React.FC = () => {
  const toast = useToast();
  const notes = usePosInformationNotes();
  const [selected, setSelected] = useState(posInformationKey("settings", "tab-org"));
  const [draft, setDraft] = useState(emptyPair);
  const [languageTab, setLanguageTab] = useState<"en" | "fr">("en");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const noteField = useRef<HTMLTextAreaElement>(null);
  const imageField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (dirty) return;
    setDraft(notePair(notes[selected], selected));
  }, [dirty, notes, selected]);

  const selectItem = (key: string) => {
    setDirty(false);
    setSelected(key);
    setDraft(notePair(notes[key], key));
  };

  const save = async () => {
    setSaving(true);
    const ref = doc(db, "systemConfig", "posInformation");
    try {
      const snap = await getDoc(ref);
      const saved = { en: draft.en, fr: draft.fr };
      if (snap.exists()) {
        await updateDoc(ref, new FieldPath("notes", selected), saved);
        await updateDoc(ref, { updatedAt: serverTimestamp() });
      } else {
        await setDoc(ref, {
          notes: { [selected]: saved },
          updatedAt: serverTimestamp(),
        });
      }
      setDirty(false);
      toast({ title: "Information saved", status: "success", duration: 2000, isClosable: true });
    } catch (error) {
      toast({
        title: "Information was not saved",
        description: error instanceof Error ? error.message : "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const insertImage = (url: string) => {
    const field = noteField.current;
    setDirty(true);
    setDraft((prev) => {
      const current = prev[languageTab];
      const start = field ? Math.min(field.selectionStart, current.length) : current.length;
      const end = field ? Math.min(field.selectionEnd, current.length) : current.length;
      const before = current.slice(0, start);
      const after = current.slice(end);
      const prefix = before.length === 0 || /\n\s*\n$/.test(before) ? "" : "\n\n";
      const suffix = after.length === 0 || /^\n\s*\n/.test(after) ? "" : "\n\n";
      return { ...prev, [languageTab]: `${before}${prefix}[img]${url}[/img]${suffix}${after}` };
    });
  };

  const uploadImage = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Choose an image file", status: "error", duration: 3000, isClosable: true });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Image must be under 5 MB", status: "error", duration: 3000, isClosable: true });
      return;
    }
    setUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_") || "image";
      const stored = ref(storage, `posInformation/${Date.now()}-${safeName}`);
      await uploadBytes(stored, file, { contentType: file.type });
      insertImage(await getDownloadURL(stored));
      toast({ title: "Image added to the note", status: "success", duration: 2000, isClosable: true });
    } catch (error) {
      toast({
        title: "Image was not uploaded",
        description: error instanceof Error ? error.message : "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
      if (imageField.current) imageField.current.value = "";
    }
  };

  const preview = formatPosInformation(draft[languageTab]);
  const selectedLabel = POS_INFORMATION_GROUPS.flatMap((group) =>
    group.items.flatMap((item) => {
      const itemKey = posInformationKey(group.id, item.id);
      const rows = [{ key: itemKey, label: `${group.label} / ${item.label}` }];
      (item.details || []).forEach((detail) => {
        rows.push({
          key: posInformationDetailKey(group.id, item.id, detail.id),
          label: `${group.label} / ${item.label} / ${detail.label}`,
        });
      });
      return rows;
    })
  ).find((item) => item.key === selected)?.label;

  return (
    <PageShell
      title="Information"
      description="Each POS menu item has its own note. A blank line starts a new paragraph."
      fluid
    >
      <Flex gap={4} wrap="wrap" align="center" mb={2} fontSize="sm">
        <Box>{formatPosInformation("[b]Bold[/b]")}</Box>
        <Box>{formatPosInformation("[big]Bigger[/big]")}</Box>
        <Box>{formatPosInformation("[h]Heading[/h]")}</Box>
        <Box>{formatPosInformation("[blue]Blue[/blue]")}</Box>
        <Box>{formatPosInformation("[orange]Orange[/orange]")}</Box>
        <Box>{formatPosInformation("[red]Red[/red]")}</Box>
        <Box>{formatPosInformation("[green]Green[/green]")}</Box>
        <Box>{formatPosInformation("[navy]Navy[/navy]")}</Box>
      </Flex>
      <Text fontSize="xs" color="gray.500" mb={4}>
        [b][/b] bold, [big][/big] bigger, [h][/h] heading, [blue][/blue], [orange][/orange], [red][/red], [green][/green], [navy][/navy], [img][/img] image
      </Text>
      <Flex gap={4} align="stretch">
        <Box
          w="280px"
          flexShrink={0}
          bg="white"
          border="1px solid"
          borderColor="#d5e4ea"
          borderRadius="lg"
          p={3}
        >
          {POS_INFORMATION_GROUPS.map((group) => (
            <Box key={group.id} mb={3}>
              <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" mb={1}>
                {group.label}
              </Text>
              {group.items.map((item) => {
                const key = posInformationKey(group.id, item.id);
                const active = key === selected;
                const hasDetails = (item.details || []).length > 0;
                const open = openGroups[key] === true;
                return (
                  <Box key={key}>
                    <Button
                      variant="ghost"
                      justifyContent="flex-start"
                      w="full"
                      h="auto"
                      minH="36px"
                      py={1.5}
                      px={2}
                      mb={0.5}
                      fontWeight={active ? "700" : "500"}
                      bg={active ? "#e7f2f6" : "transparent"}
                      color={active ? "#023047" : "inherit"}
                      onClick={() => {
                        selectItem(key);
                        if (hasDetails) setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
                      }}
                    >
                      {hasDetails ? (
                        <Box as="span" mr={2} display="inline-flex" w="17px" h="17px">
                          {React.createElement((open ? FiChevronDown : FiChevronRight) as React.ComponentType<any>, { size: 17 })}
                        </Box>
                      ) : null}
                      {item.label}
                    </Button>
                    {open ? (item.details || []).map((detail) => {
                      const detailKey = posInformationDetailKey(group.id, item.id, detail.id);
                      const detailActive = detailKey === selected;
                      return (
                        <Button
                          key={detailKey}
                          variant="ghost"
                          justifyContent="flex-start"
                          w="full"
                          h="auto"
                          minH="32px"
                          py={1}
                          pl={8}
                          pr={2}
                          mb={0.5}
                          fontSize="sm"
                          fontWeight={detailActive ? "700" : "500"}
                          bg={detailActive ? "#e7f2f6" : "transparent"}
                          color={detailActive ? "#023047" : "inherit"}
                          onClick={() => selectItem(detailKey)}
                        >
                          {detail.label}
                        </Button>
                      );
                    }) : null}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
        <Box flex={1} minW={0} bg="white" border="1px solid" borderColor="#d5e4ea" borderRadius="lg" p={4}>
          <Text fontWeight="600" mb={3}>{selectedLabel}</Text>
          <Flex mb={2} borderBottom="1px solid" borderColor="#d5e4ea">
            {(["en", "fr"] as const).map((item) => (
              <Button
                key={item}
                variant="ghost"
                borderRadius="0"
                borderBottom="2px solid"
                borderColor={languageTab === item ? "#023047" : "transparent"}
                fontWeight={languageTab === item ? "700" : "500"}
                color={languageTab === item ? "#023047" : "gray.600"}
                onClick={() => setLanguageTab(item)}
              >
                {item === "en" ? "English" : "French"}
              </Button>
            ))}
          </Flex>
          <Textarea
            ref={noteField}
            value={draft[languageTab]}
            onChange={(event) => {
              const value = event.target.value;
              setDirty(true);
              setDraft((prev) => ({ ...prev, [languageTab]: value }));
            }}
            minH="220px"
            fontFamily="mono"
            fontSize="sm"
            placeholder={languageTab === "en" ? "Type the English note." : "Tapez la note en français."}
          />
          <input
            ref={imageField}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file = event.target.files && event.target.files[0];
              if (file) uploadImage(file);
            }}
          />
          <Flex mt={3} gap={3}>
            <Button bg="#023047" color="white" _hover={{ bg: "#034e6e" }} onClick={save} isLoading={saving}>
              Save
            </Button>
            <Button variant="outline" onClick={() => imageField.current && imageField.current.click()} isLoading={uploading}>
              Upload image
            </Button>
          </Flex>
          <Text mt={2} fontSize="xs" color="gray.500">Save stores English and French together. Upload image inserts the picture at the cursor.</Text>
          <Text mt={6} mb={2} fontWeight="600">Preview</Text>
          <Box fontSize="sm" color="#1a202c">
            {preview || <Text color="gray.500">This menu item has no information yet.</Text>}
          </Box>
        </Box>
      </Flex>
    </PageShell>
  );
};

export default PosInformationPage;
